import { useMemo, useState } from 'react';
import Avatar from '@/components/base/Avatar';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import StatusBadge from '@/components/base/StatusBadge';
import { api } from '@/utils/api';
import { formatMoney, formatDate } from '@/utils/format';
import { campaignSlug } from '@/utils/campaign';
import {
  buildContractValues,
  contractFileName,
  defaultContractBody,
  defaultContractMeta,
  isHireReady,
  resolveContract,
  summarizeContracts,
} from '@/utils/contract';
import type { Campaign, CampaignContract, ContractStatus } from '@/types/portal';
import PdfPreviewModal from '@/components/feature/PdfPreviewModal';
import { textDocument } from '@/utils/pdfDocument';
import ContractPreviewModal from './ContractPreviewModal';
import SaveTemplatePanel from './SaveTemplatePanel';

interface ContractsPanelProps {
  campaign: Campaign;
  /** One per creator on the roster, from `GET /campaigns/:id/contracts`. */
  contracts: CampaignContract[];
}

interface ContractRow {
  id: string;
  creatorId: string;
  creatorName: string;
  avatar: string | null;
  handle: string;
  fileName: string;
  fileType: string;
  currency: string;
  amount: number;
  deliverables: number;
  status: ContractStatus;
  signedAt?: string;
  signedBy?: string;
  signatureRef?: string;
  sentAt?: string;
  /** The agreement text; falls back to the default template for mock data. */
  body: string;
  values: Record<string, string>;
}

const TILES: { key: keyof ReturnType<typeof summarizeContracts>; label: string; icon: string; tone: string }[] = [
  { key: 'signed', label: 'Signed', icon: 'ri-shield-check-line', tone: 'text-accent-700' },
  { key: 'awaiting', label: 'Awaiting signature', icon: 'ri-time-line', tone: 'text-secondary-900' },
  { key: 'notSent', label: 'Not sent', icon: 'ri-draft-line', tone: 'text-foreground-500' },
  { key: 'declined', label: 'Declined', icon: 'ri-close-circle-line', tone: 'text-primary-700' },
];

export default function ContractsPanel({ campaign, contracts }: ContractsPanelProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [resentId, setResentId] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  // A contract (or all of them) as a PDF document, previewed before saving.
  const [pdf, setPdf] = useState<{ title: string; html: string } | null>(null);

  const rows = useMemo<ContractRow[]>(() => {
    const contentTypes = campaign.platforms.join(', ') || 'the agreed content formats';
    return contracts.map((contract) => ({
      id: contract.id,
      creatorId: contract.creatorId,
      creatorName: contract.creatorName,
      avatar: contract.avatar,
      handle: contract.handle ?? '',
      fileName: contract.fileName || defaultContractMeta.fileName,
      fileType: contract.fileType || defaultContractMeta.fileType,
      currency: contract.currency,
      amount: contract.amount,
      deliverables: contract.deliverables,
      status: contract.status,
      signedAt: contract.signedAt,
      signedBy: contract.signedBy,
      signatureRef: contract.signatureRef,
      sentAt: contract.sentAt,
      body: contract.body ?? defaultContractBody,
      values: buildContractValues({
        creatorName: contract.creatorName,
        handle: contract.handle ?? '',
        brand: campaign.brandClient,
        campaign: campaign.name,
        currency: contract.currency,
        amount: contract.amount,
        deliverables: contract.deliverables,
        contentTypes,
        startDate: campaign.startDate,
        endDate: campaign.endDate,
      }),
    }));
  }, [contracts, campaign]);

  const summary = useMemo(() => summarizeContracts(rows), [rows]);

  const unsigned = rows.filter((row) => !isHireReady(row.status));

  const downloadRow = (row: ContractRow) => {
    const statusLine =
      row.status === 'Signed'
        ? `Signed by ${row.signedBy ?? row.creatorName} on ${
            row.signedAt ? formatDate(row.signedAt) : 'record'
          }${row.signatureRef ? ` · Reference ${row.signatureRef}` : ''}`
        : `Signature status: ${row.status} — not yet signed. This creator is not activated.`;
    setPdf({
      title: `Contract: ${row.creatorName}`,
      html: textDocument(
        { title: contractFileName(row.creatorName, campaign.name), subtitle: campaign.name },
        [{ heading: row.creatorName, body: resolveContract(row.body, row.values), note: statusLine }],
      ),
    });
  };

  const downloadAll = () => {
    const sections = rows.map((row) => {
        const statusLine =
          row.status === 'Signed'
            ? `Signed by ${row.signedBy ?? row.creatorName} on ${
                row.signedAt ? formatDate(row.signedAt) : 'record'
              }${row.signatureRef ? ` · Reference ${row.signatureRef}` : ''}`
            : `Signature status: ${row.status} — not yet signed.`;
      return { heading: row.creatorName, body: resolveContract(row.body, row.values), note: statusLine };
    });
    setPdf({
      title: 'All contracts',
      html: textDocument({ title: `${campaignSlug(campaign.name)}-contracts`, subtitle: campaign.name }, sections),
    });
  };

  const resend = async (row: ContractRow) => {
    setNotice('');
    try {
      await api(`/campaigns/${campaign.id}/contracts/${row.id}/resend`, { method: 'POST' });
      setResentId(row.creatorId);
      window.setTimeout(
        () => setResentId((current) => (current === row.creatorId ? null : current)),
        2500,
      );
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Could not resend the contract.');
    }
  };

  const active = rows.find((row) => row.creatorId === activeId) ?? null;

  if (contracts.length === 0) {
    return (
      <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-xl text-foreground-500">
          <i className="ri-file-paper-2-line" />
        </span>
        <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">
          No contracts yet
        </h3>
        <p className="mt-1 max-w-sm text-sm text-foreground-600">
          Upload a contract template and invite creators — their agreements appear here with a
          signature status.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-5 space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {TILES.map((tile) => (
          <div key={tile.key} className="rounded-lg border border-background-200 bg-background-50 p-4">
            <p className="flex items-center gap-1.5 text-xs text-foreground-500">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={tile.icon} />
              </span>
              {tile.label}
            </p>
            <p className={`mt-1 font-heading text-xl font-semibold ${tile.tone}`}>
              {summary[tile.key]}
            </p>
          </div>
        ))}
      </div>

      <SaveTemplatePanel campaignName={campaign.name} />

      {notice ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm text-primary-800"
        >
          <span className="inline-flex items-center gap-2">
            <i className="ri-error-warning-line" />
            {notice}
          </span>
          <button
            type="button"
            onClick={() => setNotice('')}
            className="cursor-pointer text-primary-700 hover:text-primary-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-heading text-sm font-semibold text-foreground-950">
            Contracts under {campaign.name}
          </h2>
          <Badge tone="accent" icon="ri-shield-check-line">
            E-signature required
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="hidden items-center gap-1.5 text-[11px] text-foreground-500 sm:inline-flex">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-file-text-line" />
            </span>
            {defaultContractMeta.fileName}
          </span>
          <Button variant="outline" size="sm" icon="ri-file-pdf-2-line" onClick={downloadAll}>
            All contracts as PDF
          </Button>
        </div>
      </div>

      {unsigned.length > 0 ? (
        <p className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2.5 text-xs leading-relaxed text-secondary-900">
          <span className="mt-0.5 w-4 h-4 flex items-center justify-center shrink-0">
            <i className="ri-lock-2-line" />
          </span>
          {unsigned.length} creator{unsigned.length === 1 ? '' : 's'} cannot be fully hired until the
          contract is signed. Their fee stays uncommitted and deliverables stay locked until
          signature.
        </p>
      ) : (
        <p className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-xs leading-relaxed text-accent-800">
          <span className="mt-0.5 w-4 h-4 flex items-center justify-center shrink-0">
            <i className="ri-checkbox-circle-line" />
          </span>
          Every creator on this campaign has signed. All engagements are fully activated.
        </p>
      )}

      <div className="overflow-x-auto rounded-lg border border-background-200 bg-background-50">
        <table className="row-accent w-full min-w-[960px] border-collapse text-left">
          <thead>
            <tr className="border-b border-background-200 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
              <th className="px-5 py-3 font-semibold">Creator</th>
              <th className="px-3 py-3 font-semibold">Contract</th>
              <th className="px-3 py-3 font-semibold">Fee</th>
              <th className="px-3 py-3 font-semibold">Deliverables</th>
              <th className="px-3 py-3 font-semibold">Signature</th>
              <th className="px-3 py-3 font-semibold">Hiring</th>
              <th className="px-5 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const hireReady = isHireReady(row.status);
              return (
                <tr key={row.id} className="border-b border-background-100 last:border-0">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar src={row.avatar} alt={row.creatorName} size="sm" />
                      <div>
                        <p className="text-sm font-medium text-foreground-950">{row.creatorName}</p>
                        <p className="text-xs text-foreground-500">{row.handle}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <p className="max-w-[200px] truncate text-sm text-foreground-700">{row.fileName}</p>
                    <p className="text-[11px] text-foreground-400">
                      {row.fileType}
                      {row.sentAt ? ` · sent ${formatDate(row.sentAt)}` : ' · not sent'}
                    </p>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground-700">
                    {formatMoney(row.amount, row.currency)}
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground-700">{row.deliverables}</td>
                  <td className="px-3 py-3">
                    <StatusBadge status={row.status} />
                    {row.status === 'Signed' && row.signedAt ? (
                      <p className="mt-1 text-[11px] text-foreground-400">
                        signed {formatDate(row.signedAt)}
                      </p>
                    ) : null}
                  </td>
                  <td className="px-3 py-3">
                    {hireReady ? (
                      <Badge tone="accent" icon="ri-check-line">
                        Activated
                      </Badge>
                    ) : (
                      <Badge tone="secondary" icon="ri-lock-2-line">
                        Signature required
                      </Badge>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => setActiveId(row.creatorId)}
                        aria-label={`Preview ${row.creatorName}'s contract`}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 cursor-pointer hover:bg-background-100"
                      >
                        <i className="ri-eye-line" />
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadRow(row)}
                        aria-label={`${row.creatorName}'s contract as PDF`}
                        title="Contract as PDF"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 cursor-pointer hover:bg-background-100 hover:text-accent-700"
                      >
                        <i className="ri-file-pdf-2-line" />
                      </button>
                      {row.status === 'Sent' || row.status === 'Viewed' || row.status === 'Declined' ? (
                        <button
                          type="button"
                          onClick={() => void resend(row)}
                          aria-label={`Resend contract to ${row.creatorName}`}
                          className="inline-flex h-8 items-center gap-1 rounded-md border border-background-300 px-2 text-[11px] font-label text-foreground-600 cursor-pointer hover:bg-background-100 hover:text-secondary-900"
                        >
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className={resentId === row.creatorId ? 'ri-check-line' : 'ri-send-plane-line'} />
                          </span>
                          {resentId === row.creatorId ? 'Resent' : 'Resend'}
                        </button>
                      ) : (
                        <span className="w-8" aria-hidden="true" />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ContractPreviewModal
        open={Boolean(active)}
        onClose={() => setActiveId(null)}
        creatorName={active?.creatorName ?? ''}
        handle={active?.handle ?? ''}
        fileName={active?.fileName ?? defaultContractMeta.fileName}
        fileType={active?.fileType ?? defaultContractMeta.fileType}
        body={active?.body ?? defaultContractBody}
        values={active?.values ?? {}}
        status={active?.status ?? 'Draft'}
        signedAt={active?.signedAt}
        signedBy={active?.signedBy}
        signatureRef={active?.signatureRef}
        onDownload={() => {
          if (!active) return;
          setActiveId(null);
          downloadRow(active);
        }}
      />

      <PdfPreviewModal
        open={pdf !== null}
        onClose={() => setPdf(null)}
        title={pdf?.title ?? ''}
        html={pdf?.html ?? ''}
      />
    </div>
  );
}